/**
 * Confidence Scoring System
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Multi-dimensional confidence scoring based on codebase familiarity
 * - Historical accuracy tracking per code pattern type
 * - Weighted scoring based on context relevance
 * - Adaptive thresholds that learn from feedback
 */

import * as fs from 'fs';
import * as path from 'path';
import {
  CodeSuggestion,
  ConfidenceFactors,
  CodebaseKnowledge,
  PatternSignature,
  AHParameterInfo,
} from './types';

export interface ConfidenceScore {
  overall: number;           // 0-100
  factors: ConfidenceFactors;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  recommendation: 'accept' | 'review' | 'reject';
  reasoning: string[];
}

interface HistoricalAccuracy {
  category: string;
  totalSuggestions: number;
  accepted: number;
  acceptedWithEdits: number;
  rejected: number;
  averageEditDistance: number;
  lastUpdated: string;
}

export class ConfidenceScorer {
  private workspaceRoot: string;
  private knowledge: CodebaseKnowledge | null = null;
  private historicalAccuracy: Map<string, HistoricalAccuracy> = new Map();
  private historyPath: string;
  
  // Adaptive thresholds (learned over time)
  private acceptThreshold = 75;
  private reviewThreshold = 50;

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.historyPath = path.join(workspaceRoot, '.guardrail', 'confidence-history.json');
    this.loadHistory();
  }

  /**
   * Calculate confidence score for a suggestion
   */
  score(suggestion: CodeSuggestion, knowledge?: CodebaseKnowledge): ConfidenceScore {
    if (knowledge) {
      this.knowledge = knowledge;
    }

    const factors = this.calculateFactors(suggestion);
    const overall = this.calculateOverall(factors);
    const grade = this.calculateGrade(overall);
    const recommendation = this.getRecommendation(overall, factors);
    const reasoning = this.generateReasoning(factors, overall);

    return {
      overall,
      factors,
      grade,
      recommendation,
      reasoning,
    };
  }

  /**
   * Calculate individual confidence factors
   */
  private calculateFactors(suggestion: CodeSuggestion): ConfidenceFactors {
    const code = suggestion.code;

    return {
      importFamiliarity: this.calculateImportFamiliarity(code),
      patternSimilarity: this.calculatePatternSimilarity(code),
      typeConsistency: this.calculateTypeConsistency(code),
      apiAccuracy: this.calculateAPIAccuracy(code),
      contextRelevance: this.calculateContextRelevance(suggestion),
      historicalAccuracy: this.calculateHistoricalAccuracy(code),
    };
  }

  /**
   * Calculate how familiar the imports are to the codebase
   */
  private calculateImportFamiliarity(code: string): number {
    if (!this.knowledge) return 50; // Neutral if no knowledge

    const imports = this.extractImports(code);
    if (imports.length === 0) return 100;

    let familiarCount = 0;
    for (const imp of imports) {
      if (this.knowledge.imports.has(imp)) {
        familiarCount++;
      } else if (this.isCommonPackage(imp)) {
        familiarCount += 0.8; // Common packages get partial credit
      }
    }

    return Math.round((familiarCount / imports.length) * 100);
  }

  /**
   * Calculate pattern similarity to existing codebase
   */
  private calculatePatternSimilarity(code: string): number {
    if (!this.knowledge || this.knowledge.patterns.length === 0) return 50;

    let matchScore = 0;
    let totalPatterns = 0;

    for (const pattern of this.knowledge.patterns) {
      try {
        const regex = new RegExp(pattern.pattern);
        if (regex.test(code)) {
          matchScore += pattern.frequency;
        }
        totalPatterns++;
      } catch {
        // Invalid pattern
      }
    }

    if (totalPatterns === 0) return 50;

    // Normalize to 0-100
    const normalized = (matchScore / totalPatterns) * 100;
    return Math.min(100, Math.round(normalized * 2)); // Scale up since matches are sparse
  }

  /**
   * Calculate type consistency with codebase types
   */
  private calculateTypeConsistency(code: string): number {
    if (!this.knowledge) return 50;

    const typeRefs = this.extractTypeReferences(code);
    if (typeRefs.length === 0) return 100;

    let consistentCount = 0;
    for (const type of typeRefs) {
      if (this.knowledge.types.has(type) || this.isBuiltInType(type)) {
        consistentCount++;
      }
    }

    return Math.round((consistentCount / typeRefs.length) * 100);
  }

  /**
   * Calculate API accuracy based on known function signatures
   */
  private calculateAPIAccuracy(code: string): number {
    if (!this.knowledge) return 50;

    const functionCalls = this.extractFunctionCalls(code);
    if (functionCalls.length === 0) return 100;

    let accurateCount = 0;
    for (const call of functionCalls) {
      const funcInfo = this.knowledge.functions.get(call.name);
      if (funcInfo) {
        // Check argument count
        const minArgs = funcInfo.parameters.filter((p: AHParameterInfo) => !p.optional).length;
        const maxArgs = funcInfo.parameters.length;
        
        if (call.argCount >= minArgs && call.argCount <= maxArgs) {
          accurateCount++;
        } else {
          accurateCount += 0.3; // Partial credit for known function with wrong args
        }
      } else if (this.isBuiltInFunction(call.name)) {
        accurateCount += 0.8; // Partial credit for built-ins
      }
    }

    return Math.round((accurateCount / functionCalls.length) * 100);
  }

  /**
   * Calculate context relevance
   */
  private calculateContextRelevance(suggestion: CodeSuggestion): number {
    let score = 50; // Start neutral

    // Language consistency
    if (suggestion.language === 'typescript' || suggestion.language === 'javascript') {
      score += 10;
    }

    // File path relevance (if provided)
    if (suggestion.filePath) {
      const ext = path.extname(suggestion.filePath);
      if (['.ts', '.tsx', '.js', '.jsx'].includes(ext)) {
        score += 10;
      }
    }

    // Code length (very short or very long is suspicious)
    const lineCount = suggestion.code.split('\n').length;
    if (lineCount >= 3 && lineCount <= 100) {
      score += 15;
    } else if (lineCount > 100) {
      score -= 10; // Long suggestions more likely to have issues
    }

    // Check for documentation/comments
    if (suggestion.code.includes('//') || suggestion.code.includes('/*')) {
      score += 5;
    }

    // Check for error handling
    if (suggestion.code.includes('try') || suggestion.code.includes('catch') || suggestion.code.includes('.catch(')) {
      score += 10;
    }

    return Math.min(100, Math.max(0, score));
  }

  /**
   * Calculate based on historical accuracy for similar code
   */
  private calculateHistoricalAccuracy(code: string): number {
    const category = this.categorizeCode(code);
    const history = this.historicalAccuracy.get(category);

    if (!history || history.totalSuggestions < 5) {
      return 50; // Not enough data
    }

    const acceptRate = (history.accepted + history.acceptedWithEdits * 0.7) / history.totalSuggestions;
    return Math.round(acceptRate * 100);
  }

  /**
   * Calculate overall confidence score
   */
  private calculateOverall(factors: ConfidenceFactors): number {
    // Weighted average with importance-based weights
    const weights = {
      importFamiliarity: 0.20,
      patternSimilarity: 0.15,
      typeConsistency: 0.15,
      apiAccuracy: 0.25,
      contextRelevance: 0.10,
      historicalAccuracy: 0.15,
    };

    const overall = 
      factors.importFamiliarity * weights.importFamiliarity +
      factors.patternSimilarity * weights.patternSimilarity +
      factors.typeConsistency * weights.typeConsistency +
      factors.apiAccuracy * weights.apiAccuracy +
      factors.contextRelevance * weights.contextRelevance +
      factors.historicalAccuracy * weights.historicalAccuracy;

    return Math.round(overall);
  }

  /**
   * Calculate letter grade
   */
  private calculateGrade(overall: number): 'A' | 'B' | 'C' | 'D' | 'F' {
    if (overall >= 90) return 'A';
    if (overall >= 80) return 'B';
    if (overall >= 70) return 'C';
    if (overall >= 60) return 'D';
    return 'F';
  }

  /**
   * Get recommendation based on score and factors
   */
  private getRecommendation(overall: number, factors: ConfidenceFactors): 'accept' | 'review' | 'reject' {
    // Hard reject if any critical factor is too low
    if (factors.importFamiliarity < 30 || factors.apiAccuracy < 30) {
      return 'reject';
    }

    if (overall >= this.acceptThreshold) {
      return 'accept';
    } else if (overall >= this.reviewThreshold) {
      return 'review';
    }
    return 'reject';
  }

  /**
   * Generate human-readable reasoning
   */
  private generateReasoning(factors: ConfidenceFactors, overall: number): string[] {
    const reasons: string[] = [];

    if (factors.importFamiliarity >= 80) {
      reasons.push('✓ Uses familiar imports from this codebase');
    } else if (factors.importFamiliarity < 50) {
      reasons.push('⚠ Contains unfamiliar imports - verify they exist');
    }

    if (factors.patternSimilarity >= 70) {
      reasons.push('✓ Follows established code patterns');
    } else if (factors.patternSimilarity < 40) {
      reasons.push('⚠ Code style differs from codebase patterns');
    }

    if (factors.typeConsistency >= 80) {
      reasons.push('✓ Type usage is consistent with codebase');
    } else if (factors.typeConsistency < 50) {
      reasons.push('⚠ Uses types not found in codebase');
    }

    if (factors.apiAccuracy >= 80) {
      reasons.push('✓ API calls match known signatures');
    } else if (factors.apiAccuracy < 50) {
      reasons.push('⚠ API calls may have incorrect signatures');
    }

    if (factors.historicalAccuracy >= 70) {
      reasons.push('✓ Similar code has been accepted before');
    } else if (factors.historicalAccuracy < 40) {
      reasons.push('⚠ Similar code often rejected/edited');
    }

    if (overall >= 80) {
      reasons.unshift(`High confidence (${overall}%) - likely correct`);
    } else if (overall >= 60) {
      reasons.unshift(`Medium confidence (${overall}%) - review recommended`);
    } else {
      reasons.unshift(`Low confidence (${overall}%) - careful review needed`);
    }

    return reasons;
  }

  /**
   * Record feedback to improve future scoring
   */
  recordFeedback(suggestion: CodeSuggestion, accepted: boolean, edited: boolean, editDistance?: number): void {
    const category = this.categorizeCode(suggestion.code);
    
    let history = this.historicalAccuracy.get(category);
    if (!history) {
      history = {
        category,
        totalSuggestions: 0,
        accepted: 0,
        acceptedWithEdits: 0,
        rejected: 0,
        averageEditDistance: 0,
        lastUpdated: new Date().toISOString(),
      };
    }

    history.totalSuggestions++;
    if (accepted) {
      if (edited) {
        history.acceptedWithEdits++;
        if (editDistance !== undefined) {
          history.averageEditDistance = 
            (history.averageEditDistance * (history.acceptedWithEdits - 1) + editDistance) / history.acceptedWithEdits;
        }
      } else {
        history.accepted++;
      }
    } else {
      history.rejected++;
    }
    history.lastUpdated = new Date().toISOString();

    this.historicalAccuracy.set(category, history);
    this.saveHistory();

    // Adapt thresholds based on feedback
    this.adaptThresholds();
  }

  /**
   * Adapt thresholds based on accumulated feedback
   */
  private adaptThresholds(): void {
    let totalAccepted = 0;
    let totalRejected = 0;

    this.historicalAccuracy.forEach(h => {
      totalAccepted += h.accepted + h.acceptedWithEdits;
      totalRejected += h.rejected;
    });

    const total = totalAccepted + totalRejected;
    if (total < 20) return; // Not enough data

    const acceptRate = totalAccepted / total;

    // If accept rate is high, we can be more lenient
    if (acceptRate > 0.8) {
      this.acceptThreshold = Math.max(60, this.acceptThreshold - 2);
      this.reviewThreshold = Math.max(40, this.reviewThreshold - 2);
    }
    // If accept rate is low, be more strict
    else if (acceptRate < 0.5) {
      this.acceptThreshold = Math.min(85, this.acceptThreshold + 2);
      this.reviewThreshold = Math.min(60, this.reviewThreshold + 2);
    }
  }

  /**
   * Helper methods
   */
  private extractImports(code: string): string[] {
    const imports: string[] = [];
    const importRegex = /import\s+.*?\s+from\s+['"]([^'"]+)['"]/g;
    const requireRegex = /require\s*\(\s*['"]([^'"]+)['"]\s*\)/g;

    let match;
    while ((match = importRegex.exec(code)) !== null) {
      imports.push(match[1].split('/')[0].replace(/^@/, '@').split('/').slice(0, 2).join('/'));
    }
    while ((match = requireRegex.exec(code)) !== null) {
      imports.push(match[1].split('/')[0]);
    }

    return [...new Set(imports)];
  }

  private extractTypeReferences(code: string): string[] {
    const types: string[] = [];
    const typeRegex = /:\s*(\w+)(?:<|>|\[|\]|\s|,|;|\))/g;
    
    let match;
    while ((match = typeRegex.exec(code)) !== null) {
      if (!this.isPrimitiveType(match[1])) {
        types.push(match[1]);
      }
    }

    return [...new Set(types)];
  }

  private extractFunctionCalls(code: string): Array<{ name: string; argCount: number }> {
    const calls: Array<{ name: string; argCount: number }> = [];
    const funcRegex = /(\w+)\s*\(([^)]*)\)/g;

    let match;
    while ((match = funcRegex.exec(code)) !== null) {
      const name = match[1];
      const args = match[2].trim();
      if (!['if', 'for', 'while', 'switch', 'catch', 'function', 'return'].includes(name)) {
        calls.push({
          name,
          argCount: args ? args.split(',').length : 0,
        });
      }
    }

    return calls;
  }

  private categorizeCode(code: string): string {
    if (code.includes('async') || code.includes('await') || code.includes('Promise')) {
      return 'async';
    }
    if (code.includes('useState') || code.includes('useEffect') || code.includes('React')) {
      return 'react';
    }
    if (code.includes('express') || code.includes('app.get') || code.includes('router')) {
      return 'express';
    }
    if (code.includes('SELECT') || code.includes('INSERT') || code.includes('prisma')) {
      return 'database';
    }
    if (code.includes('describe') || code.includes('test(') || code.includes('expect')) {
      return 'testing';
    }
    if (code.includes('interface') || code.includes('type ') || code.includes(': {')) {
      return 'types';
    }
    return 'general';
  }

  private isCommonPackage(name: string): boolean {
    const common = [
      'react', 'react-dom', 'next', 'express', 'lodash', 'axios', 'moment',
      'date-fns', 'uuid', 'zod', 'yup', 'joi', 'prisma', 'mongoose', 'typeorm',
      'jest', 'mocha', 'chai', 'vitest', 'typescript', 'webpack', 'vite',
      'tailwindcss', 'styled-components', 'emotion', 'framer-motion',
    ];
    return common.includes(name.replace(/^@\w+\//, ''));
  }

  private isPrimitiveType(type: string): boolean {
    return ['string', 'number', 'boolean', 'void', 'any', 'unknown', 'never', 'null', 'undefined', 'object'].includes(type);
  }

  private isBuiltInType(type: string): boolean {
    const builtIns = [
      'Promise', 'Array', 'Map', 'Set', 'Date', 'RegExp', 'Error',
      'Record', 'Partial', 'Required', 'Pick', 'Omit', 'Exclude', 'Extract',
      'ReturnType', 'Parameters', 'InstanceType', 'Readonly', 'NonNullable',
      'HTMLElement', 'Event', 'Response', 'Request',
    ];
    return builtIns.includes(type) || this.isPrimitiveType(type);
  }

  private isBuiltInFunction(name: string): boolean {
    const builtIns = [
      'console', 'Math', 'JSON', 'Object', 'Array', 'String', 'Number',
      'parseInt', 'parseFloat', 'isNaN', 'isFinite', 'fetch', 'setTimeout',
      'setInterval', 'clearTimeout', 'clearInterval', 'Promise', 'require',
    ];
    return builtIns.includes(name);
  }

  private loadHistory(): void {
    try {
      if (fs.existsSync(this.historyPath)) {
        const data = JSON.parse(fs.readFileSync(this.historyPath, 'utf-8'));
        this.historicalAccuracy = new Map(Object.entries(data.history || {}));
        this.acceptThreshold = data.acceptThreshold || 75;
        this.reviewThreshold = data.reviewThreshold || 50;
      }
    } catch (e) {
      console.error('Failed to load confidence history:', e);
    }
  }

  private saveHistory(): void {
    try {
      const dir = path.dirname(this.historyPath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }
      
      const data = {
        history: Object.fromEntries(this.historicalAccuracy),
        acceptThreshold: this.acceptThreshold,
        reviewThreshold: this.reviewThreshold,
        lastUpdated: new Date().toISOString(),
      };
      
      fs.writeFileSync(this.historyPath, JSON.stringify(data, null, 2));
    } catch (e) {
      console.error('Failed to save confidence history:', e);
    }
  }

  /**
   * Update codebase knowledge
   */
  updateKnowledge(knowledge: CodebaseKnowledge): void {
    this.knowledge = knowledge;
  }

  /**
   * Get current thresholds
   */
  getThresholds(): { accept: number; review: number } {
    return {
      accept: this.acceptThreshold,
      review: this.reviewThreshold,
    };
  }

  /**
   * Get historical accuracy stats
   */
  getStats(): { categories: HistoricalAccuracy[]; overall: { accepted: number; rejected: number; total: number } } {
    const categories = Array.from(this.historicalAccuracy.values());
    const overall = {
      accepted: categories.reduce((sum, h) => sum + h.accepted + h.acceptedWithEdits, 0),
      rejected: categories.reduce((sum, h) => sum + h.rejected, 0),
      total: categories.reduce((sum, h) => sum + h.totalSuggestions, 0),
    };
    return { categories, overall };
  }
}
